import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Container, Handshake, IdCard, Package, Search, Truck, type LucideIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useSearch } from '@/data/useSearch';
import { MIN_QUERY, searchHref, type SearchHit, type SearchKind } from '@/lib/search';
import { cn } from '@/lib/utils';

/**
 * The global search (change spec 2026-09-24/topbar, design 1.5): a button in the top bar that reads
 * like a search box, and a command palette that opens from it or from Ctrl/Cmd+K anywhere in the
 * portal. The palette is an ARIA combobox (input + listbox, aria-activedescendant): arrows move the
 * active hit, Enter opens it, Esc closes. Below sm the dialog is a full-screen sheet (the on-screen
 * keyboard takes half the phone); from sm it is a panel under the top bar.
 */

const KIND_ICON: Record<SearchKind, LucideIcon> = {
  load: Package,
  driver: IdCard,
  carrier: Truck,
  truck: Container,
  broker: Handshake,
};

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

export function GlobalSearch() {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    // Cmd+K on a Mac, Ctrl+K elsewhere: on a Mac, Ctrl+K in a text field is "delete to end of line".
    const mac = isMac();
    const onKey = (e: KeyboardEvent) => {
      const mod = mac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
      if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={t('search.open')}
        className="flex h-touch w-full min-w-0 items-center gap-2 rounded-md border border-hairline bg-sunken px-3 text-left text-base text-ink-muted hover:border-ink-muted"
      >
        <Search className="size-5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">
          <span className="sm:hidden">{t('search.short')}</span>
          <span className="hidden sm:inline">{t('topbar.searchPlaceholder')}</span>
        </span>
        <kbd className="hidden shrink-0 rounded-sm border border-hairline px-1.5 text-xs font-medium text-ink-muted md:inline">
          {isMac() ? '⌘K' : 'Ctrl K'}
        </kbd>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        {open && <SearchPalette onClose={() => setOpen(false)} />}
      </Dialog>
    </>
  );
}

function SearchPalette({ onClose }: { onClose(): void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = React.useState('');
  const [active, setActive] = React.useState(0);
  const state = useSearch(q);
  // UX audit portal-documentation H4: the hits grouped by kind ("Loads · 4") in the order the kinds first
  // appear; the arrows walk the groups in the same order they read.
  const groups = React.useMemo(() => groupHits(state.status === 'ready' ? state.hits : []), [state]);
  const hits = React.useMemo(() => groups.flatMap((g) => g.hits), [groups]);
  const listId = React.useId();
  const optionId = (i: number) => `${listId}-opt-${i}`;

  // A new answer starts at its best hit.
  React.useEffect(() => setActive(0), [state]);

  const go = (hit: SearchHit | undefined) => {
    if (!hit) return;
    onClose();
    navigate(searchHref(hit));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (hits.length) setActive((i) => (i + 1) % hits.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (hits.length) setActive((i) => (i - 1 + hits.length) % hits.length);
    } else if (e.key === 'Home' && hits.length) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End' && hits.length) {
      e.preventDefault();
      setActive(hits.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(hits[active]);
    }
  };

  // Keep the active hit in view while the arrows walk the list.
  React.useEffect(() => {
    document.getElementById(`${listId}-opt-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, listId]);

  const expanded = hits.length > 0;

  return (
    <DialogContent
      showCloseButton={false}
      className={cn(
        'top-0 bottom-0 flex h-dvh max-h-none flex-col gap-0 rounded-none p-0 pb-0',
        'sm:top-20 sm:bottom-auto sm:h-auto sm:max-h-[min(34rem,calc(100dvh-7rem))] sm:max-w-xl sm:translate-y-0 sm:rounded-lg sm:pb-0',
      )}
    >
      <DialogTitle className="sr-only">{t('search.title')}</DialogTitle>
      <DialogDescription className="sr-only">{t('search.help')}</DialogDescription>
      <div className="flex items-center gap-2 border-b border-hairline px-4 pt-[env(safe-area-inset-top)]">
        <Search className="size-5 shrink-0 text-ink-muted" aria-hidden="true" />
        <input
          autoFocus
          role="combobox"
          aria-label={t('topbar.searchLabel')}
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={expanded ? optionId(active) : undefined}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('topbar.searchPlaceholder')}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="go"
          className="h-14 min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-ink-muted"
        />
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-md px-2 py-1 text-sm font-medium text-ink-secondary hover:bg-secondary"
        >
          <span className="sm:hidden">{t('common.cancel')}</span>
          <kbd className="hidden sm:inline">Esc</kbd>
        </button>
      </div>

      <div className="relative min-h-0 flex-1 overflow-y-auto p-2">
        <div id={listId} role="listbox" aria-label={t('search.results')} className={cn(!expanded && 'hidden')}>
          {groups.map((g) => (
            <div key={g.kind} role="group" aria-labelledby={`${listId}-g-${g.kind}`} className="pb-1">
              <p id={`${listId}-g-${g.kind}`} className="px-3 pt-2 pb-1 text-xs font-semibold tracking-wide text-ink-muted uppercase">
                {t('search.group', { kind: t(`search.kindsPlural.${g.kind}`), count: g.hits.length })}
              </p>
              {g.hits.map((hit) => {
                const i = hits.indexOf(hit);
                const Icon = KIND_ICON[hit.kind];
                return (
                  <div
                    key={`${hit.kind}:${hit.id}`}
                    id={optionId(i)}
                    role="option"
                    aria-selected={i === active}
                    onMouseMove={() => setActive(i)}
                    onClick={() => go(hit)}
                    className={cn(
                      'flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 py-1.5',
                      i === active ? 'bg-secondary' : 'hover:bg-secondary',
                    )}
                  >
                    <Icon className="size-4 shrink-0 text-ink-muted" aria-hidden="true" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-semibold text-ink">{hit.title}</span>
                      {hit.subtitle && <span className="truncate text-xs text-ink-muted">{hit.subtitle}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <SearchStatus state={state.status} query={q} empty={state.status === 'ready' && hits.length === 0} />
      </div>
      {expanded && (
        <p aria-hidden="true" className="hidden border-t border-hairline px-4 py-2 text-xs text-ink-muted sm:block">
          {t('search.keys')}
        </p>
      )}
    </DialogContent>
  );
}

function SearchStatus({ state, query, empty }: { state: string; query: string; empty: boolean }) {
  const { t } = useTranslation();
  let text: string | null = null;
  if (query.trim().length < MIN_QUERY) text = t('search.hint');
  else if (state === 'error') text = t('search.error');
  else if (empty) text = t('search.empty', { query: query.trim() });
  else if (state === 'loading') text = t('search.loading');
  if (!text) return null;
  return (
    <p role="status" className="px-3 py-6 text-center text-sm text-ink-muted">
      {text}
    </p>
  );
}

/** The hits grouped by kind, the kinds in the order they first appear, each group in the server's order. */
export function groupHits(hits: readonly SearchHit[]): { kind: SearchKind; hits: SearchHit[] }[] {
  const groups: { kind: SearchKind; hits: SearchHit[] }[] = [];
  for (const h of hits) {
    const g = groups.find((x) => x.kind === h.kind);
    if (g) g.hits.push(h);
    else groups.push({ kind: h.kind, hits: [h] });
  }
  return groups;
}
